import { useContext, useEffect, useMemo, useRef } from 'react';
import { css, cx } from '@emotion/css';
import { Divider, EmptyState, LiveFeedback, LoadingSpinner, ToggleViewControl } from '@/components';
import nocloudsIllustration from '@/assets/noclouds.png';
import { FlyerList } from '@/components/FlyerboardPagination/FlyerList';
import { BodyMediumMidEmphasis, ButtonFlatMedium } from '@/design-system';
import AuthenticationContext from '@/context/AuthenticationContext';
import { useJarFlyerboardContext } from '@/context/JarFlyerboardContext';
import { useJarFlyerboardPagination, useLiveFeedback, useURLSearchParams } from '@/hooks';
import { GET_USER_FAVORITE_ORGS } from '@/graphql/queries';
import { AllFlyerFilters } from '@/graphql/types';
import type { GetUserFavoriteOrgsResponse } from '@/graphql/types';
import { useQuery } from '@apollo/client';
import colors from '@/theme/colors';
import { TrackingStage, TrackingEvents, EventDetails } from '@/config/tracking';

interface JarFollowingOrgsListProps {
  onBrowseAll: () => void;
}

const trackingFrom = {
  event_from: TrackingEvents.viewFlyerSet,
  event_detail_from: EventDetails.viewFlyerSet('jar'),
  stage_from: TrackingStage.discovery,
};

export const JarFollowingOrgsList: React.FC<JarFollowingOrgsListProps> = ({ onBrowseAll }) => {
  const { audienceId } = useURLSearchParams();
  const { isAuthenticated } = useContext(AuthenticationContext);
  const [liveMessage, setLiveMessage] = useLiveFeedback({ delay: 2500 });
  const { filters, districtId } = useJarFlyerboardContext();

  const { data: favoriteOrgsData } = useQuery<GetUserFavoriteOrgsResponse>(GET_USER_FAVORITE_ORGS, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated,
  });

  const owningIds = useMemo(
    () => favoriteOrgsData?.getUserFavoriteOrgs?.orgs?.map(org => org.orgId.toString()),
    [favoriteOrgsData]
  );

  const followingOrgsFilters = useMemo<AllFlyerFilters>(
    () => ({
      ...(filters || { schoolIds: [] }),
      ...(owningIds && owningIds.length > 0 && { owningIds }),
    }),
    [filters, owningIds]
  );

  const { flyers, loading, fetchMoreFlyers, total, dataNotFound } = useJarFlyerboardPagination({
    audienceId,
    districtId,
    flyerboardFilters: followingOrgsFilters,
    skip: !filters,
  });

  const wasLoadingRef = useRef(false);

  useEffect(() => {
    if (wasLoadingRef.current && !loading) {
      setLiveMessage(flyers.length > 0 ? 'Flyers loaded.' : 'No flyers found.');
    }
    wasLoadingRef.current = loading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const isPaginationEnd = flyers.length >= total && !loading;

  return (
    <main className={cn.container}>
      <LiveFeedback message={liveMessage} />
      <div className={cn.toolbar}>
        <ToggleViewControl trackingFrom={trackingFrom} />
      </div>
      {loading && flyers.length === 0 && <LoadingSpinner />}
      {dataNotFound && (
        <EmptyState
          title="No active flyers from your followed organizations right now"
          description="Check back at a later time."
          illustration={nocloudsIllustration}
          bottom={
            <div className={cn.browseAllFlyerEmptyState}>
              <ButtonFlatMedium onClick={onBrowseAll}>Browse All Flyers</ButtonFlatMedium>
            </div>
          }
        />
      )}
      {flyers.length > 0 && (
        <FlyerList
          flyers={flyers}
          nextPage={fetchMoreFlyers}
          total={total}
          trackId="jar-following-orgs"
          audienceId={audienceId}
          districtId={districtId}
          trackingEventFrom="jar-following-orgs"
          showTags
          showNewBadge
          showOrgName
        />
      )}
      {isPaginationEnd && flyers.length > 0 && (
        <>
          <Divider />
          <div className={cn.footer}>
            <BodyMediumMidEmphasis color={colors.slate}>
              That's everything from your followed organizations.
            </BodyMediumMidEmphasis>
            <BodyMediumMidEmphasis color={colors.prussian}>Want to see more?</BodyMediumMidEmphasis>
            <ButtonFlatMedium onClick={onBrowseAll} className={cn.browseAllLink}>
              Browse All Flyers
            </ButtonFlatMedium>
          </div>
        </>
      )}
    </main>
  );
};

const cn = {
  container: css`
    flex: 1 1 auto;
    display: flex;
    flex-flow: column nowrap;
    width: 100%;
    flex-grow: 10;
    margin-top: 16px;
  `,
  toolbar: css`
    display: flex;
    justify-content: flex-end;
    padding-bottom: 16px;
  `,
  footer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 16px 0px;
    gap: 16px;
    text-align: center;
    margin-bottom: 64px;
  `,
  browseAllLink: css`
    &.MuiButtonBase-root {
      padding: 0 !important;
      height: auto;
    }
  `,
  browseAllFlyerEmptyState: css`
    padding-top: 8px;
  `,
};
